import React, { useCallback, useEffect, useState } from 'react';
import { useModalStore, useStore } from 'src/hooks/useStore';
import Input from 'src/components/ui-kit/Input';
import DatePicker from 'src/components/ui-kit/DatePicker';
import SearchPage, {
  FormItem,
  Title,
  Content,
  Symbol,
  Button,
  FooterLink,
  ErrorMsg,
} from './SearchPage';
import ReactSelect from 'src/components/ui-kit/ReactSelect';

import { globalComponents } from 'src/types/globalTypes';
import { CustomRadioButton } from 'src/styles/radio';
import {
  ApplNow,
  TrademarkSearchNoOptions,
  TrademarkSearchNameOptions,
  TmarktypeOptions,
  IReqTmarkRightsPage,
  TmarkOrderOptions,
} from 'src/types/api/trademarkRightsApiTypes';
import { FaArrowRight } from 'react-icons/fa';
import { formateDate, stringToDate } from 'src/utils/formatters';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from 'src/components/layout/pagination/Pagination';
import QueryString from 'qs';
import AlertModal from 'src/components/ui-kit/AlertModal';
import styled from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { mobilePoint } from 'src/styles/globalStyle';
import Modal from 'src/components/ui-kit/Modal';
import Tooltip from 'src/components/ui-kit/Tooltip';
import { FaExclamationCircle } from 'react-icons/fa';

const ToolText = styled.div`
  font-size: 18px;
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  color: #3387ff;
`;

const ModalWrapper = styled.div`
  width: 100%;
  padding: 32px 40px;
  text-align: center;
`;

const ModalContent = styled.div`
  display: flex;
  font-size: 16px;
  line-height: 40px;
  color: ${colors.BlueGray_800};
  gap: 5px;
  text-align: left;

  @media (max-width: ${mobilePoint}) {
    display: grid;
  }
`;

const ModalButtonDiv = styled.div`
  display: grid;
  gap: 1em;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
`;

const ModalButton = styled.button<{ isCancel?: boolean }>`
  width: max-content;
  height: 40px;
  border-radius: 4px;
  background-color: ${(prop) => (prop.isCancel ? colors.notice_orange : colors.primaryGreen_400)};
  color: ${colors.white};
  font-size: 18px;
  margin-top: 2em;
  padding: 0 1em;
  cursor: pointer;
`;

/**
 * 商標搜尋頁面
 */
const TrademarkRights: React.FC = () => {
  const closeModal = useModalStore((state) => state.closeModal);
  const isMobile = useStore((state) => state.isMobile);
  const navigate = useNavigate();
  const { control, register, reset, getValues } = useForm<IReqTmarkRightsPage>();
  //日期間隔符號
  const dateInterval = '';
  // 欄位檢測
  const [isCheckedOk, setIsCheckedOk] = useState<boolean | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const setActiveModal = useModalStore((state) => state.setActiveModal);

  // 頁面元素資訊
  const component = globalComponents.T;

  // 因重複過多次跑sonar issue，進行重構
  const datePickerFun = (onChange: (e: any) => void, value?: string, datePickerId?: string) => (
    <DatePicker
      width="45%"
      onChange={(date) => onChange(formateDate(date, dateInterval))}
      selected={stringToDate(value)}
      primaryColors={component.color}
	  datePickerId={datePickerId}
    />
  );

  const inputFun = (onChange: (e: any) => void, inputId?: string) => (
    <Input
      primaryColors={component.color}
      onChange={onChange}
      width="100%"
      isBigForm={isMobile ? true : false}
	  inputId={inputId}
    />
  );

  /** 取得當前欄位應有字數 */
  const getNum = (type: string) => {
    switch (type) {
      case 'examno':
        return 8;
      case 'applno':
        return 9;
    }
  };

  /** 商標查詢欄位檢測 */
  const checkNumTmark = (type: string, content: string | null) => {
    let flag = true;
    if (content) {
      const words = content.split(',');
      const num = getNum(type);
      for (const item of words) {
        const regex = new RegExp('^[a-zA-Z\\d]{' + num + '}$');
        flag = regex.test(item);
        if (!flag) {
          break;
        }
      }
    }
    return flag;
  };

  /** 欄位是否至少輸入一項(不包含商標樣態) */
  const checkIfSearchLeastOne = (params: IReqTmarkRightsPage) => {
    const checkKeywordValue = (param: [string, any]) => {
      let flag = false;
  
      if (param[1]) {
        // 因部分欄位值的型態為物件，若非物件則可直接判斷有值；為物件，則檢查物件項目的值是否存在
        flag = typeof param[1] === 'object' ? !!param[1].value : true;
      }
  
      return flag;
    };

    const searchParams = Object.entries(params).filter(
      (item) => item[0] !== 'tmarkType' && checkKeywordValue(item),
    ).length;

    return searchParams >= 5;
  };

  /** 日期欄位是否為合法範圍 */
  const checkIfDateRangeLegal = (regNoticeBDate: string | undefined, regNoticeEDate: string | undefined, deadlineBDate: string | undefined, deadlineEDate: string | undefined) => {
    let publishDate = true;
    let chargeExpirDate = true;

    if (regNoticeBDate && regNoticeEDate) {
      const date1 = new Date(`${regNoticeBDate.slice(0, 4)}-${regNoticeBDate.slice(4, 6)}-${regNoticeBDate.slice(6)}`);
      const date2 = new Date(`${regNoticeEDate.slice(0, 4)}-${regNoticeEDate.slice(4, 6)}-${regNoticeEDate.slice(6)}`);
      publishDate = date2.getFullYear() - date1.getFullYear() < 5;
    }

    if (deadlineBDate && deadlineEDate) {
      const date1 = new Date(`${deadlineBDate.slice(0, 4)}-${deadlineBDate.slice(4, 6)}-${deadlineBDate.slice(6)}`);
      const date2 = new Date(`${deadlineEDate.slice(0, 4)}-${deadlineEDate.slice(4, 6)}-${deadlineEDate.slice(6)}`);
      chargeExpirDate = date2.getFullYear() - date1.getFullYear() < 5;
    }
  
    return publishDate && chargeExpirDate;
  };

  /** 驗證日期是否僅選起日或僅選迄日 */
  const checkIfDateSetBothSide = (regNoticeBDate: string | undefined, regNoticeEDate: string | undefined, deadlineBDate: string | undefined, deadlineEDate: string | undefined) => {
    let isOnlySetOneSide = true;

    if ((regNoticeBDate === undefined && regNoticeEDate !== undefined) || (regNoticeBDate !== undefined && regNoticeEDate === undefined) || (deadlineBDate === undefined && deadlineEDate !== undefined) || (deadlineBDate !== undefined && deadlineEDate === undefined)) {
      isOnlySetOneSide = false;
    }

    return isOnlySetOneSide;
  };

  const setDateRange = (regNoticeBDate: string | undefined, regNoticeEDate: string | undefined, deadlineBDate: string | undefined, deadlineEDate: string | undefined) => {
    var modalRegNoticeBDate = ''
    var modalRegNoticeEDate = ''
    var modalDeadlineBDate = ''
    var modalDeadlineEDate = ''

    if ((regNoticeBDate === undefined && regNoticeEDate !== undefined)) {
      modalRegNoticeBDate = (parseInt(regNoticeEDate.substring(0, 4)) - 5) + regNoticeEDate.substring(4, 8)
      modalRegNoticeEDate = regNoticeEDate
    }

    if ((regNoticeBDate !== undefined && regNoticeEDate === undefined)) {
      modalRegNoticeBDate = regNoticeBDate
      modalRegNoticeEDate = (parseInt(regNoticeBDate.substring(0, 4)) + 5) + regNoticeBDate.substring(4, 8)
    }

    if ((deadlineBDate === undefined && deadlineEDate !== undefined)) {
      modalDeadlineBDate = (parseInt(deadlineEDate.substring(0, 4)) - 5) + deadlineEDate.substring(4, 8)
      modalDeadlineEDate = deadlineEDate
    }

    if ((deadlineBDate !== undefined && deadlineEDate === undefined)) {
      modalDeadlineBDate = deadlineBDate
      modalDeadlineEDate = (parseInt(deadlineBDate.substring(0, 4)) + 5) + deadlineBDate.substring(4, 8)
    }

    return <Modal isAlert={true} width={'28em'}>
      <ModalWrapper>
        <ModalContent>因搜尋日期區間限制5年，將以以下日期範圍查詢。</ModalContent>
        <ModalContent>
          {modalRegNoticeBDate && modalRegNoticeEDate ? `公告日期：${modalRegNoticeBDate} ~ ${modalRegNoticeEDate}` : ''}
        </ModalContent>
        <ModalContent>
          {modalDeadlineBDate && modalDeadlineEDate ? `專用期限：${modalDeadlineBDate} ~ ${modalDeadlineEDate}` : ''}
        </ModalContent>
        <ModalButtonDiv>
          <ModalButton
            onClick={() => {
              setIsCheckedOk(true);
              closeModal();
            }}
          >
            確定
          </ModalButton>
          <ModalButton isCancel onClick={() => closeModal()}>
            關閉
          </ModalButton>
        </ModalButtonDiv>
      </ModalWrapper>
    </Modal>
  }

  /** 處理插入錯誤訊息 */
  const handleErrMsg = useCallback(() => {
    setErrorMsg(
      TrademarkSearchNoOptions.find((item) => item.value === getValues('searchNo.keyWord'))?.label +
      '輸入格式有誤（' +
      getNum(getValues('searchNo.keyWord')?.toString() ?? "") +
      '碼英數字）',
    );
  }, [getValues]);

  //插入預設值
  useEffect(() => {
    reset({
      pageNo: FIRST_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: TmarkOrderOptions[0].value,
      searchNo: { keyWord: TrademarkSearchNoOptions[0].value },
      searchName: { keyWord: TrademarkSearchNameOptions[0].value },
    });
  }, [reset]);

  // 進行查詢
  useEffect(() => {
    isCheckedOk &&
      navigate({
        pathname: process.env.PUBLIC_URL + '/trademarkRights/result',
        search: QueryString.stringify(getValues()),
      });
  }, [isCheckedOk, navigate, getValues]);

  // 插入錯誤訊息
  useEffect(() => {
    isCheckedOk === false && handleErrMsg();
  }, [isCheckedOk, handleErrMsg]);

  const annotation = (
    <div>
      本系統資訊僅供參考，所有資料以經濟部智慧財產局公告為準。
      <br />
      連結參考政府資料開放平臺下載網址：
      <FooterLink href="https://data.gov.tw/dataset/30117" target="_blank">
        商標權狀態異動資料
      </FooterLink>
    </div>
  );

  const form = (
    <>
      <FormItem>
        <Title required>權利是否存續(必填)</Title>
        <Content>
          {Object.keys(ApplNow).map((key, index) => (
            <CustomRadioButton primaryColors={component.color} key={key}>
              <label>
                <input
                  type="radio"
                  value={key}
                  defaultChecked={index === 0}
				  title={`權利是否存續選項-${ApplNow[key as keyof typeof ApplNow]}`}
                  {...register('isEffective')}
                />
                <span />
                {`${ApplNow[key as keyof typeof ApplNow]}`}
              </label>
            </CustomRadioButton>
          ))}
        </Content>
      </FormItem>
      <FormItem>
        <Title>
          <Controller
            control={control}
            name={`searchNo.keyWord`}
            render={({ field: { onChange, value } }) => (
              <ReactSelect
                options={TrademarkSearchNoOptions}
                primaryColors={component.color}
                onChange={onChange}
                value={value}
                isBigForm={isMobile ? true : false}
				inputId="input-searchNo-keyWord"
				ariaLabelValue="搜尋號下拉式選單"
              />
            )}
          />
        </Title>
        <Content isWrong={!!getValues('searchNo.value') && !!errorMsg}>
          <Controller
            control={control}
            name={`searchNo.value`}
            render={({ field: { onChange } }) => (
              <Input
                placeholder={"多筆以','隔開"}
                primaryColors={component.color}
                onChange={onChange}
                width="100%"
                isBigForm={isMobile ? true : false}
				inputId="input-searchNo-value"
				ariaLabel="輸入號碼"
              />
            )}
          />
          <ErrorMsg>{getValues('searchNo.value') && errorMsg}</ErrorMsg>
        </Content>
      </FormItem>
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`regNoticeBDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle={'公告日期'}
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
            <Controller
              control={control}
              name={`regNoticeEDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle={'至'}
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>
          <Title>
            <label htmlFor="datePicker-regNoticeBDate">公告日期</label>
            <div style={{ float: 'right' }} title="公告日期搜尋區間為5年">
              <Tooltip overlay={"公告日期搜尋區間為5年"} bgColor={component.color.sixth}>
                <ToolText>
                  <Icon>
                    <FaExclamationCircle />
                  </Icon>
                </ToolText>
              </Tooltip>
            </div>
          </Title>
          <Content>
            <Controller
              control={control}
              name={`regNoticeBDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-regNoticeBDate')}
            />
            <Symbol><label htmlFor="datePicker-regNoticeEDate">~</label></Symbol>
            <Controller
              control={control}
              name={`regNoticeEDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-regNoticeEDate')}
            />
          </Content>
        </FormItem>
      )}
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`tmarkName`}
              render={({ field: { onChange } }) => (
                <Input
                  primaryColors={component.color}
                  customizedTitle="商標中英文名稱"
                  onChange={onChange}
                  isBigForm
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>
          <Title><label htmlFor="input-tmarkName">商標中英文名稱</label></Title>
          <Content>
            <Controller
              control={control}
              name={`tmarkName`}
              render={({ field: { onChange } }) => inputFun(onChange, 'input-tmarkName')}
            />
          </Content>
        </FormItem>
      )}
      <FormItem>
        <Title>
          <Controller
            control={control}
            name={`searchName.keyWord`}
            render={({ field: { onChange, value } }) => (
              <ReactSelect
                options={TrademarkSearchNameOptions}
                primaryColors={component.color}
                onChange={onChange}
                value={value}
                isBigForm={isMobile ? true : false}
				inputId="input-searchName-keyWord"
				ariaLabelValue="搜尋名稱下拉式選單"
              />
            )}
          />
        </Title>
        <Content>
          <Controller
            control={control}
            name={`searchName.value`}
            render={({ field: { onChange } }) => inputFun(onChange, 'input-searchName')}
          />
        </Content>
      </FormItem>
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`deadlineBDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle="專用期限"
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
            <Controller
              control={control}
              name={`deadlineEDate`}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  primaryColors={component.color}
                  placeholderTitle="至"
                  onChange={(date) => onChange(formateDate(date, dateInterval))}
                  selected={stringToDate(value)}
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>
          <Title>
            <label htmlFor="datePicker-deadlineBDate">專用期限</label>
            <div style={{ float: 'right' }} title="專用期限搜尋區間為5年">
              <Tooltip overlay={"專用期限搜尋區間為5年"} bgColor={component.color.sixth}>
                <ToolText>
                  <Icon>
                    <FaExclamationCircle />
                  </Icon>
                </ToolText>
              </Tooltip>
            </div>
          </Title>
          <Content>
            <Controller
              control={control}
              name={`deadlineBDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-deadlineBDate')}
            />
            <Symbol><label htmlFor="datePicker-deadlineEDate">~</label></Symbol>
            <Controller
              control={control}
              name={`deadlineEDate`}
              render={({ field: { onChange, value } }) => datePickerFun(onChange, value, 'datePicker-deadlineEDate')}
            />
          </Content>
        </FormItem>
      )}
      {isMobile ? (
        <FormItem>
          <Content>
            <Controller
              control={control}
              name={`tmarkType`}
              render={({ field: { onChange, value } }) => (
                <ReactSelect
                  options={TmarktypeOptions}
                  primaryColors={component.color}
                  customizedTitle={'商標樣態'}
                  onChange={onChange}
                  value={value}
                  width="100%"
                  isBigForm
				  inputId="input-tmarkType"
				  ariaLabelValue="商標樣態下拉式選單"
                />
              )}
            />
          </Content>
        </FormItem>
      ) : (
        <FormItem>
          <Title><label htmlFor="input-tmarkType">商標樣態</label></Title>
          <Content>
            <Controller
              control={control}
              name={`tmarkType`}
              render={({ field: { onChange, value } }) => (
                <ReactSelect
                  options={TmarktypeOptions}
                  primaryColors={component.color}
                  onChange={onChange}
                  value={value}
                  width="100%"
				  inputId="input-tmarkType"
				  ariaLabelValue="商標樣態下拉式選單"
                />
              )}
            />
          </Content>
        </FormItem>
      )}
    </>
  );

  return (
    <SearchPage
      title={component.title}
      annotation={annotation}
      bgImg={isMobile ? component.bgImgBannerMobile : component.bgImgBanner}
      image={component.hoverImage}
      bgColor={component.color.third}
      searchPageImgs={component.searchPageImgs}
    >
      {form}
      <Button
        onClick={() => {
          // 欄位檢查
          setIsCheckedOk(
            checkIfDateRangeLegal(getValues('regNoticeBDate'), getValues('regNoticeEDate'), getValues('deadlineBDate'), getValues('deadlineEDate')) &&
            checkIfDateSetBothSide(getValues('regNoticeBDate'), getValues('regNoticeEDate'), getValues('deadlineBDate'), getValues('deadlineEDate')) &&
            checkNumTmark(getValues('searchNo.keyWord')?.toString() ?? "", getValues('searchNo.value')?.toString() ?? "") &&
            checkIfSearchLeastOne(getValues()),
          );
          isCheckedOk === false && handleErrMsg();
          // 跳出提醒視窗
          !checkIfSearchLeastOne(getValues()) &&
            setActiveModal(
              <AlertModal>
                <div>請至少再輸入一項查詢條件。</div>
              </AlertModal>,
            );
            // 驗證日期是否僅選起日或僅選迄日
            !checkIfDateSetBothSide(getValues('regNoticeBDate'), getValues('regNoticeEDate'), getValues('deadlineBDate'), getValues('deadlineEDate')) &&
            setActiveModal(setDateRange(getValues('regNoticeBDate'), getValues('regNoticeEDate'), getValues('deadlineBDate'), getValues('deadlineEDate')));
            // 驗證日期區間是否和規
            !checkIfDateRangeLegal(getValues('regNoticeBDate'), getValues('regNoticeEDate'), getValues('deadlineBDate'), getValues('deadlineEDate')) &&
              setActiveModal(
                <AlertModal width="21em">
                  <div>搜尋日期區間超過5年請重新選擇。</div>
                </AlertModal>,
              );
        }}
      >
        搜尋
        <span>
          <FaArrowRight />
        </span>
      </Button>
    </SearchPage >
  );
};

export default TrademarkRights;
